import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TIMEOUT_MS } from 'src/app/core/http/http-context.tokens';

/** Default stall budget for any request that does not override it. */
export const DEFAULT_TIMEOUT_MS = 20_000;

/** Long-running calls that legitimately take longer than the default. */
export const UPLOAD_TIMEOUT_MS = 120_000;
export const BULK_TIMEOUT_MS = 60_000;

/**
 * Custodial (Fireblocks) operations.
 *
 * `each` degrades to a total-duration cap on this endpoint: it is a single POST that
 * emits no intermediate events while the backend signs, broadcasts and then polls for
 * a receipt, so there is nothing to keep extending the budget. The client budget must
 * therefore be at least the server's own, or the browser aborts a transaction that is
 * already on chain and the user is shown a failure for a transfer that succeeds.
 *
 * The server budget is `waitForTransactionConfirmation`'s 300s of receipt polling on
 * top of an unbounded Fireblocks signing phase, with `server.setTimeout(600000)` as
 * the hard stop. Matching that 600s means this timeout only ever fires once the
 * server has already abandoned the socket - a genuinely dead connection, which is
 * what the interceptor is for.
 */
export const TRANSACTION_TIMEOUT_MS = 600_000;

/**
 * Every endpoint that blocks on a Fireblocks round trip. Derived by auditing the
 * backend for controllers that reach the remote co-signer - getFireblocksWeb3Provider,
 * getFireblocksWeb3WithHealthyRPC, getFireblocksSDK - and keeping the ones this app
 * calls:
 *   /contract-transaction                    sign, broadcast, poll receipt (300s)
 *   /fire-block/storage-payment-signature    personal_sign via the Fireblocks provider
 *   /fire-block/withdraw-to-external-wallet  Fireblocks SDK, moves funds out of custody
 *   /fireblock-signature                     eth_signTypedData_v4, regulated loan bids
 *   /nft-bid-signature                       eth_signTypedData_v4, NFT bids
 *   /open-loan-request-bid-signature         eth_signTypedData_v4, open loan bids
 *
 * Deliberately absent: /lazy-mint-signature signs locally with an ethers Wallet and
 * never leaves the server, and PATCH /nft/expired-dates is already covered by the
 * bulk rule below.
 */
const CUSTODIAL_ENDPOINT_PATTERN =
  /\/contract-transaction|\/fire-block\/|\/fireblock-signature|\/nft-bid-signature|\/open-loan-request-bid-signature/;

@Injectable()
export class TimeoutInterceptor implements HttpInterceptor {

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const each = this.resolveTimeout(req);

    return next.handle(req).pipe(
      /**
       * `each` bounds the gap *between* events rather than the total duration, so a
       * slow-but-progressing transfer on a throttled connection is not killed — only a
       * genuinely stalled socket is. Upload progress events keep extending their own
       * budget for free, which is what makes the 120s file-upload ceiling a safety net
       * rather than a hard cap.
       */
      timeout({
        each,
        with: () => throwError(() => this.asTimeoutResponse(req, each))
      })
    );
  }

  /**
   * Emitting a real HttpErrorResponse (rather than RxJS's bare TimeoutError) is what
   * keeps this change invisible to callers: CommonService.getNetworkErrorMessage() and
   * TelegramErrorReporterService's `408: 'Request timeout'` mapping already handle the
   * status, so no component needs a per-call change.
   */
  private asTimeoutResponse(req: HttpRequest<unknown>, each: number): HttpErrorResponse {
    return new HttpErrorResponse({
      status: 408,
      statusText: 'Request Timeout',
      url: req.urlWithParams,
      error: { message: `Request timed out after ${each}ms` }
    });
  }

  private resolveTimeout(req: HttpRequest<unknown>): number {
    const explicit = req.context.get(TIMEOUT_MS);
    if (explicit !== null && explicit > 0) {
      return explicit;
    }

    if (this.isCustodialTransaction(req)) {
      return TRANSACTION_TIMEOUT_MS;
    }

    if (this.isFileUpload(req)) {
      return UPLOAD_TIMEOUT_MS;
    }

    if (this.isBulkOperation(req)) {
      return BULK_TIMEOUT_MS;
    }

    return DEFAULT_TIMEOUT_MS;
  }

  /**
   * Every custodial endpoint, not just the on-chain write.
   *
   * A single custodial payment is a sequence of Fireblocks round trips - approve,
   * sign, submit - and each one blocks on the same remote co-signer. Naming only
   * /contract-transaction moved the failure to the next step in the sequence rather
   * than fixing it: every signature endpoint above calls `web3.currentProvider.request`
   * against the Fireblocks provider, and withdraw-to-external-wallet drives the
   * Fireblocks SDK. All of them are bounded on the server only by
   * `server.setTimeout(600000)`.
   *
   * Matched centrally rather than by an HttpContext at each call site: a custodial
   * call submitted without the longer budget is silently wrong in a way that costs
   * the user money, so a future call site must not be able to opt in by omission.
   * An explicit TIMEOUT_MS still overrides this.
   */
  private isCustodialTransaction(req: HttpRequest<unknown>): boolean {
    return req.method === 'POST' && CUSTODIAL_ENDPOINT_PATTERN.test(req.url);
  }

  private isFileUpload(req: HttpRequest<unknown>): boolean {
    return req.method === 'POST' && req.url.startsWith(`${environment.FILE_UPLOAD}/file-upload/document`);
  }

  /** Bulk custody sync and select-all listings routinely exceed the default. */
  private isBulkOperation(req: HttpRequest<unknown>): boolean {
    if (req.method === 'PATCH' && req.url.includes('/nft/expired-dates')) {
      return true;
    }
    return req.urlWithParams.includes('select_all=true');
  }
}
